import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Building2, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';

interface CompanySuspendedScreenProps {
  companyId?: string;
}

interface CompanyInfo {
  name: string;
  status: string;
  suspension_reason: string | null;
  suspension_type: string | null;
  suspended_at: string | null;
}

interface SuspensionRequest {
  id: string;
  status: string;
  reason: string;
  attachment_url: string | null;
  attachment_name: string | null;
  superuser_notes: string | null;
  cooldown_expires_at: string | null;
  created_at: string;
  reviewed_at: string | null;
}

const ATTACHMENT_BUCKET = 'reactivation-attachments';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const CompanySuspendedScreen: React.FC<CompanySuspendedScreenProps> = ({ companyId }) => {
  const params = useParams<{ companyId: string }>();
  const resolvedCompanyId = companyId || params.companyId || '';
  const navigate = useNavigate();
  const { user, signOut, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [request, setRequest] = useState<SuspensionRequest | null>(null);
  const [attachmentSignedUrl, setAttachmentSignedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Form state
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (profile && profile.status === 'active') {
      navigate('/dashboard', { replace: true });
    }
  }, [profile?.status]);

  const loadData = async () => {
    console.log('[CompanySuspendedScreen] loadData called', {
      resolvedCompanyId,
      userId: user?.id,
      hasUser: !!user
    });
    if (!user?.id) return;
    setLoading(true);
    try {
      const [companyResult, requestResult] = await Promise.all([
        supabase
          .from('companies')
          .select('name, status, suspension_reason, suspension_type, suspended_at')
          .eq("id", resolvedCompanyId)
          .single(),
        supabase
          .from('company_suspension_requests')
          .select('*')
          .eq("company_id", resolvedCompanyId)
          .eq('requested_by', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      console.log('[CompanySuspendedScreen] companyResult:', {
        data: companyResult.data,
        error: companyResult.error
      });
      console.log('[CompanySuspendedScreen] requestResult:', {
        data: requestResult.data,
        error: requestResult.error
      });

      // Fetch current user's role in the company
      const { data: roleData } = await supabase
        .rpc('get_my_company_role' as any, { p_company_id: resolvedCompanyId });

      setIsAdmin(roleData === 'admin');
      setCompany(companyResult.data as any);
      setRequest(requestResult.data as any);

      // Generate signed URL if request has attachment
      const reqData = requestResult.data as any;
      if (reqData?.attachment_url) {
        try {
          const urlParts = reqData.attachment_url.split(
            '/storage/v1/object/public/reactivation-attachments/'
          );
          const storagePath = urlParts[1] || reqData.attachment_url;

          const { data: signedData } = await supabase.storage
            .from('reactivation-attachments')
            .createSignedUrl(storagePath, 3600);

          if (signedData?.signedUrl) {
            setAttachmentSignedUrl(signedData.signedUrl);
          }
        } catch (e) {
          console.error('Failed to generate signed URL for attachment', e);
        }
      } else {
        setAttachmentSignedUrl(null);
      }

      // If company is no longer suspended, 
      // redirect to dashboard immediately
      if ((companyResult.data as any)?.status === 'active') {
        // Company is not suspended — this user was 
        // individually suspended. Route to the 
        // individual suspension screen.
        navigate('/suspended', { replace: true });
        return;
      }

      console.log('[CompanySuspendedScreen] state set, isPending will be:',
        requestResult.data?.status === 'pending'
      );
    } catch (e) {
      console.error('[CompanySuspendedScreen] load error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedCompanyId, user?.id]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data } = await supabase.rpc('get_user_security_status');
        const status = (data as any)?.status;
        if (status === 'active') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('[SuspendedScreen] Status check failed:', err);
      }
    };
    checkStatus();
    pollingRef.current = setInterval(checkStatus, 10000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_FILE_SIZE) {
      toast.error('File must be smaller than 5MB');
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    if (!user?.id) return;

    setSubmitting(true);
    try {
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;

      if (file) {
        const path = `${resolvedCompanyId}/${user.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from(ATTACHMENT_BUCKET)
          .upload(path, file);
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from(ATTACHMENT_BUCKET)
          .getPublicUrl(path);
        attachmentUrl = urlData.publicUrl;
        attachmentName = file.name;
      }

      const { error } = await supabase.rpc(
        'submit_company_reactivation_request' as any,
        {
          p_company_id: resolvedCompanyId,
          p_reason: reason,
          p_attachment_url: attachmentUrl,
          p_attachment_name: attachmentName,
        } as any
      );
      if (error) throw error;

      toast.success('Reactivation request submitted');
      setReason('');
      setFile(null);
      await loadData();
    } catch (e: any) {
      console.error('[CompanySuspendedScreen] submit error', e);
      toast.error(e?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!request) return;
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('company_suspension_requests')
        .update({ status: 'withdrawn' })
        .eq('id', request.id);
      if (error) throw error;
      toast.success('Request withdrawn');
      await loadData();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to withdraw');
    } finally {
      setSubmitting(false);
    }
  };

  // Determine state
  const now = new Date();
  const cooldownActive =
    request?.status === 'rejected' &&
    request.cooldown_expires_at &&
    new Date(request.cooldown_expires_at) > now;
  const isPending = request?.status === 'pending';
  const showForm = !isPending && !cooldownActive;

  const renderForm = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="reason">Reason / message <span className="text-red-600">*</span></Label>
        <Textarea
          id="reason"
          placeholder="Explain why your company should be reactivated…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="mt-2"
        />
      </div>
      <div>
        <Label htmlFor="attachment">Attachment (PDF or image, max 5MB)</Label>
        <Input
          ref={fileInputRef}
          id="attachment"
          type="file"
          accept="application/pdf,image/*"
          onChange={handleFileChange}
          className="mt-2"
        />
        {file && (
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-gray-500 flex-1 truncate">
              {file.name}
            </p>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="text-xs text-red-500 hover:text-red-700 shrink-0"
            >
              Remove
            </button>
          </div>
        )}
      </div>
      <Button
        onClick={handleSubmit}
        disabled={submitting || !reason.trim()}
        className="w-full"
      >
        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Submit reactivation request
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <Building2 className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            {isPending
              ? 'Reactivation request pending'
              : cooldownActive
              ? 'Reactivation request rejected'
              : 'Company account suspended'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="text-center text-gray-600">
                <p className="mb-2">
                  <strong>{company?.name}</strong> has been suspended and members cannot access the system.
                </p>
                {company?.suspension_reason && (
                  <div className="mb-4 p-3 bg-gray-100 rounded-lg text-left">
                    <p className="text-sm font-medium text-gray-700 mb-1">Suspension reason:</p>
                    <p className="text-sm text-gray-600">{company.suspension_reason}</p>
                  </div>
                )}
              </div>

              {/* STATE B: pending */}
              {isPending && request && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">
                        Awaiting review
                      </p>
                      <p className="text-blue-700">
                        Submitted {formatDistanceToNow(
                          new Date(request.created_at),
                          { addSuffix: true }
                        )}
                      </p>
                    </div>
                  </div>
                  {request.reason && (
                    <div className="text-sm">
                      <p className="font-medium text-gray-700 mb-1">
                        Your message:
                      </p>
                      <p className="text-gray-600 bg-white rounded border border-blue-100 p-2 whitespace-pre-wrap">
                        {request.reason}
                      </p>
                    </div>
                  )}
                  {request.attachment_name && (
                    <div className="text-sm">
                      <p className="font-medium text-gray-700 mb-1">
                        Attachment:
                      </p>
                      {attachmentSignedUrl ? (
                        <a
                          href={attachmentSignedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline hover:text-blue-800 text-xs"
                        >
                          {request.attachment_name}
                        </a>
                      ) : (
                        <p className="text-gray-600 text-xs">
                          {request.attachment_name}
                        </p>
                      )}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleWithdraw}
                    disabled={submitting}
                  >
                    {submitting && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Withdraw request
                  </Button>
                </div>
              )}

              {/* STATE C: rejected within cooldown */}
              {cooldownActive && request && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-900">Request rejected</p>
                      {request.superuser_notes && (
                        <p className="text-amber-800 mt-1">{request.superuser_notes}</p>
                      )}
                      <p className="text-amber-700 mt-2">
                        You can resubmit {formatDistanceToNow(new Date(request.cooldown_expires_at!), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* STATE A: form */}
              {showForm && isAdmin && renderForm()}

              {showForm && !isAdmin && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">Reactivation request required</p>
                  <p>Only company admins can submit a reactivation request. Please contact your company administrator.</p>
                </div>
              )}

              <Button
                onClick={() => signOut()}
                variant="outline"
                className="w-full"
              >
                Sign Out
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySuspendedScreen;
